/**
 * Delivery pricing config — flat-fee distance tiers instead of a
 * baseFee + per-mile formula. Each tier's `upTo` is an inclusive upper
 * bound in miles; the first tier whose `upTo` the distance fits under
 * wins. `freeDeliveryRules` are checked first, in order — the first
 * matching rule (order subtotal at/above `minSubtotal` AND distance
 * at/under `withinMiles`) makes delivery free regardless of which tier
 * the distance would otherwise fall into.
 */
window.LUCIOL_DELIVERY_RATES = window.LUCIOL_DELIVERY_RATES || {
  currency: 'USD',
  tiers: [
    { upTo: 3, price: 12 },
    { upTo: 6, price: 15 },
    { upTo: 8, price: 18 },
    { upTo: 10, price: 21 },
    { upTo: 12, price: 24 },
    { upTo: 15, price: 27 },
    { upTo: 18, price: 30 },
    { upTo: 21, price: 32 },
    { upTo: 24, price: 35 },
    { upTo: 27, price: 38 },
    { upTo: 30, price: 40 },
  ],
  maxMiles: 30,
  freeDeliveryRules: [
    { minSubtotal: 300, withinMiles: 10 },
    { minSubtotal: 100, withinMiles: 3 },
  ],
  fallbackLabel: 'Calculated at checkout',
  outOfRangeLabel: 'Delivery is not available for this address',
};

/**
 * Same-day lead-time rules: how far ahead of "now" a slot's start time
 * must be before it's allowed to show up in the picker at all. Delivery
 * needs an hour to prepare the order; Pickup needs two. Shared between
 * this file and cart-delivery-calculator.js (which reads the same
 * global with its own fallback in case this file hasn't run yet).
 */
window.LUCIOL_LEAD_TIME = window.LUCIOL_LEAD_TIME || {
  deliveryHours: 1,
  pickupHours: 2,
  // Seattle, not the visitor's device — a customer ordering from a
  // different timezone (or a phone/laptop with the wrong clock) must
  // not get a different cutoff than a local customer would.
  storeTimeZone: 'America/Los_Angeles',
};

/**
 * "Now", as wall-clock date + minutes-since-midnight in the store's own
 * timezone — independent of whatever timezone/clock the visitor's
 * device is set to. Slot times ("09:00" etc.) are already store-local
 * wall-clock times, so comparing in these terms means no Date-object
 * timezone math is needed anywhere else.
 */
function getStoreNow(timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date());
  const map = {};
  parts.forEach((p) => { map[p.type] = p.value; });
  let hour = Number(map.hour);
  if (hour === 24) hour = 0; // some environments format midnight as "24"
  return {
    dateStr: `${map.year}-${map.month}-${map.day}`,
    minutes: hour * 60 + Number(map.minute),
  };
}

// Zapiet's calendar API returns `disabled` as an array of
// [year, month, day] tuples with a ZERO-INDEXED month (e.g. [2026, 9, 7]
// for October 7, 2026 — confirmed by calling the API directly), NOT
// "YYYY-MM-DD" strings. Both flatpickr's `disable` option and our own
// `.includes(dateInput.value)` checks need real date strings, so every
// consumer of a calendar response's `disabled` field must run it
// through this first. Mirrors cart-delivery-calculator.js's helper.
function normalizeDisabledDates(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => {
    if (Array.isArray(entry) && entry.length >= 3) {
      const [y, m, d] = entry;
      return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
    return entry;
  });
}

// Groups Zapiet's flat `slots` array (each entry: { day_of_week,
// available_from, available_until, id, ... }, one entry per window per
// weekday) into the { monday: [...], tuesday: [...], ... } shape
// defaultDeliverySchedule() below already returns. Confirmed via a
// direct API call that Zapiet's delivery calendar endpoint returns this
// for the WHOLE week, not just the "nearest available" date — so this
// is the real, live source of truth whenever the request succeeds.
// Mirrors cart-delivery-calculator.js's helper.
function groupSlotsByWeekday(slots) {
  const result = {};
  slots.forEach((s) => {
    const day = s.day_of_week;
    if (!day || !s.available_from || !s.available_until) return;
    if (!result[day]) result[day] = [];
    result[day].push({ id: String(s.id || ''), from: s.available_from, until: s.available_until });
  });
  return result;
}

// Zapiet's pickup calendar gives `daysOfWeek`, a single { min, max }
// open/close window per weekday, rather than a flat slot list like
// delivery — individual slots are hourly within it (confirmed against a
// real minDateSlots response). Expands that into the same hourly-slot
// shape defaultPickupSchedule() below already uses. Mirrors
// cart-delivery-calculator.js's helper.
function buildPickupScheduleFromDaysOfWeek(daysOfWeek) {
  if (!daysOfWeek || typeof daysOfWeek !== 'object') return null;
  const result = {};
  Object.keys(daysOfWeek).forEach((day) => {
    const range = daysOfWeek[day];
    const startH = Number(range?.min?.hour);
    const startM = Number(range?.min?.minute || 0);
    const endH = Number(range?.max?.hour);
    const endM = Number(range?.max?.minute || 0);
    if (!Number.isFinite(startH) || !Number.isFinite(endH)) return;
    const slots = [];
    let h = startH;
    let m = startM;
    let idx = 0;
    while (h * 60 + m < endH * 60 + endM) {
      const from = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      let nh = h + 1;
      let nm = m;
      if (nh * 60 + nm > endH * 60 + endM) { nh = endH; nm = endM; }
      const until = `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
      slots.push({ id: `pickup-${day}-${idx}`, from, until });
      h = nh; m = nm; idx += 1;
    }
    result[day] = slots;
  });
  return result;
}

/**
 * Shared sessionStorage key so the cart-level delivery calculator
 * (cart-delivery-calculator.js) can pick up an address entered here on
 * the product page, within the same browser tab session.
 */
const CART_SESSION_KEY = 'luciol_cart_delivery_address';

/**
 * "Delivery fee as a real cart line item" — the trick used by e.g.
 * charlsflowers.com: since Shopify's Storefront Cart API can't set an
 * arbitrary price on a line item (only Admin API / Shopify Functions
 * can, and neither is safe/available to call from theme JS), the fee is
 * instead represented by a hidden product priced at a small fixed unit
 * (default $0.10) added with quantity = fee ÷ unitPrice. $42.60 becomes
 * 426 units of the $0.10 "Home Delivery" product — same math the
 * reference site's checkout shows (see line-item quantities there).
 *
 * TODO: set variantId once the "Home Delivery" product exists in
 * Shopify Admin (Products > Add product, price $0.10, keep it on the
 * Online Store sales channel — required for cart/add.js to accept it —
 * but don't add it to any collection/nav so customers never browse to
 * it directly). Until variantId is set, syncDeliveryFeeItem() is a
 * no-op so nothing breaks.
 */
window.LUCIOL_DELIVERY_FEE_ITEM = window.LUCIOL_DELIVERY_FEE_ITEM || {
  variantId: 45807813296175, // "Home Delivery" product, $0.10/unit
  unitPrice: 0.10,
};

async function getCartRoot() {
  return window.Shopify?.routes?.root || '/';
}

async function findDeliveryFeeLine(variantId) {
  const res = await fetch(`${await getCartRoot()}cart.js`);
  const cart = await res.json();
  return cart.items.find((item) => String(item.variant_id) === String(variantId)) || null;
}

/**
 * Adds/updates/removes the hidden delivery-fee line item so its
 * quantity always matches the currently calculated fee. feeDollars <= 0
 * (or unconfigured variantId) removes/no-ops it.
 */
async function syncDeliveryFeeItem(feeDollars) {
  const config = window.LUCIOL_DELIVERY_FEE_ITEM || {};
  const variantId = config.variantId;
  if (!variantId) return; // not set up in Admin yet — do nothing
  const unitPrice = Number(config.unitPrice) || 0.10;
  const quantity = Math.max(0, Math.round((Number(feeDollars) || 0) / unitPrice));
  const root = await getCartRoot();
  try {
    const line = await findDeliveryFeeLine(variantId);
    if (quantity <= 0) {
      if (line) {
        await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 0 }),
        });
      }
      return;
    }
    if (line) {
      if (line.quantity !== quantity) {
        await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: line.key, quantity }),
        });
      }
    } else {
      await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity, properties: { _hidden_fee: 'delivery' } }] }),
      });
    }
  } catch (err) {
    console.error('[DeliveryFeeItem] sync failed', err);
  }
}

function clearDeliveryFeeItem() {
  return syncDeliveryFeeItem(0);
}

window.syncDeliveryFeeItem = syncDeliveryFeeItem;
window.clearDeliveryFeeItem = clearDeliveryFeeItem;

// Turns a plain address string into structured components via Google's
// Geocoder — needed because this.lastPlaceAddressComponents used to only
// ever get set inside the Places Autocomplete's own place_changed
// listener. A customer who types a full address and hits Enter/clicks
// Validate without picking an autocomplete suggestion (or a staff-sent
// prefill on the /cart page reusing this same address string) left
// Delivery-Address1/City/Province/Zip/Country blank, which is what the
// checkout-button's cart-attribute fallback in cart-drawer.liquid reads.
// Resolves to null (never rejects) — a geocoding hiccup shouldn't block
// checking delivery eligibility, which is the more critical half.
function geocodeAddress(query) {
  return new Promise((resolve) => {
    if (!window.google?.maps?.Geocoder) {
      resolve(null);
      return;
    }
    new google.maps.Geocoder().geocode({ address: query }, (results, status) => {
      if (status !== 'OK' || !results?.[0]?.address_components) {
        resolve(null);
        return;
      }
      resolve(results[0].address_components);
    });
  });
}

function extractAddressComponents(components) {
  if (!Array.isArray(components)) return null;
  const get = (type, useShort) => {
    const match = components.find((c) => c.types?.includes(type));
    return match ? (useShort ? match.short_name : match.long_name) : '';
  };
  const streetNumber = get('street_number');
  const route = get('route');
  return {
    address1: [streetNumber, route].filter(Boolean).join(' '),
    city: get('locality') || get('postal_town') || get('sublocality'),
    province: get('administrative_area_level_1', true),
    zip: get('postal_code'),
    country: get('country', true),
  };
}

function saveToCartSession(address, components) {
  try {
    sessionStorage.setItem(CART_SESSION_KEY, JSON.stringify({ address, components }));
  } catch (err) {
    console.info('[ProductDeliverySelector] sessionStorage unavailable.', err);
  }
}

class ProductDeliverySelector {
  constructor(element) {
    this.element = element;
    this.form = element.closest('form[action*="/cart/add"]');
    if (!this.form) return;

    this.shopDomain = window.Shopify?.shop || '';
    this.method = 'delivery';
    this.isSaving = false;
    this.isLoadingPickup = false;
    this.isLoadingDelivery = false;
    this.deliveryLocationId = element.dataset.deliveryLocationId || '300262';
    this.storeAddress = element.dataset.storeAddress || '';
    this.productPriceCents = Number(element.dataset.variantPrice || 0);
    this.currency = element.dataset.currency || 'USD';

    this.methodButtons = element.querySelectorAll('[data-delivery-method]');
    this.deliveryPanel = element.querySelector('[data-delivery-panel]');
    this.pickupPanel = element.querySelector('[data-pickup-panel]');
    this.deliveryDate = element.querySelector('[data-delivery-date]');
    this.deliveryTime = element.querySelector('[data-delivery-time]');
    this.deliveryAddress = element.querySelector('[data-delivery-address]');
    this.deliveryAddressSubmit = element.querySelector('[data-delivery-address-submit]');
    this.deliverySelectedAddress = element.querySelector('[data-delivery-selected-address]');
    this.deliverySelectedAddressText = element.querySelector('[data-delivery-selected-address-text]');
    this.deliveryAddressStatus = element.querySelector('[data-delivery-address-status]');
    this.deliverySummary = element.querySelector('[data-delivery-summary]');
    this.deliveryRateText = element.querySelector('[data-delivery-rate-text]');
    this.orderTotalText = element.querySelector('[data-order-total-text]');
    this.deliveryMap = element.querySelector('[data-delivery-map]');
    this.deliveryMapFrame = element.querySelector('[data-delivery-map-frame]');
    this.deliveryLocation = null;
    this.deliveryValidatedAddress = '';
    this.deliveryRate = null;
    this.deliveryPostalCode = '';
    this.selectedLatLng = null;
    this.lastPlaceAddressComponents = null;
    this.googleAutocomplete = null;

    this.pickupLocation = element.querySelector('[data-pickup-location]');
    this.pickupDate = element.querySelector('[data-pickup-date]');
    this.pickupTime = element.querySelector('[data-pickup-time]');
    this.pickupCompany = element.querySelector('[data-pickup-company]');
    this.pickupAddress = element.querySelector('[data-pickup-address]');
    this.pickupCity = element.querySelector('[data-pickup-city]');
    this.pickupMapLink = element.querySelector('[data-pickup-map-link]');
    this.pickupLocations = [];
    this.pickupCalendar = null;

    this.zapietIdInput = this.form.querySelector('[data-product-zapiet-id]');
    this.errorElement = element.querySelector('[data-delivery-error]');
    this.propertyInputs = this.ensurePropertyInputs();

    this.deliverySchedule = this.defaultDeliverySchedule();
    this.pickupSchedule = this.defaultPickupSchedule();
    this.setMinimumDates();
    this.renderDeliverySlotsForDate();
    this.bindEvents();
    this.initializePickup();
    this.initializeDeliveryCalendar();
    this.initializeAddressAutocomplete();
    this.initializeDatePickers();
  }

  // Native <input type="date"> can't be restyled (colors/font/behavior
  // are OS/browser-controlled) and its calendar doesn't auto-close on
  // selection. Flatpickr replaces it with a fully CSS-stylable calendar
  // that closes itself once a date is picked, while keeping the same
  // underlying Y-m-d value the rest of this file already reads.
  initializeDatePickers() {
    if (!window.flatpickr) return;
    // No onChange handlers here on purpose: flatpickr dispatches a real
    // native 'change' event on the original input when a date is picked,
    // which the existing listeners in bindEvents() already handle.
    const shared = {
      altInput: true,
      altInputClass: 'product-delivery-control flatpickr-alt-input',
      altFormat: 'd / m / Y',
      dateFormat: 'Y-m-d',
      minDate: 'today',
      disableMobile: true,
    };
    if (this.deliveryDate) flatpickr(this.deliveryDate, shared);
    if (this.pickupDate) flatpickr(this.pickupDate, shared);
  }

  ensurePropertyInputs() {
    // Property names prefixed with "_" are stored on the order (visible
    // in Admin, webhooks, Zapiet) but Shopify automatically hides them
    // from the cart and checkout line-item display — exactly what's
    // wanted here, since these were showing up as extra text under the
    // product in checkout's order summary.
    const names = ['_Delivery address','_Delivery date','_Delivery time','_Estimated delivery fee'];
    const result = {};
    names.forEach((name) => {
      let input = this.form.querySelector(`[data-delivery-property="${name}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${name}]`;
        input.dataset.deliveryProperty = name;
        this.form.appendChild(input);
      }
      result[name] = input;
    });
    return result;
  }

  // LAST-RESORT fallback if the Zapiet calendar request fails outright
  // (network error, API outage) — groupSlotsByWeekday(data.slots) in
  // initializeDeliveryCalendar() replaces this with the real, live
  // weekly schedule whenever the request succeeds.
  defaultDeliverySchedule() {
    const slots = [
      {id:'139607615',from:'09:00',until:'15:00'},
      {id:'139607616',from:'11:00',until:'17:00'},
      {id:'139607617',from:'15:00',until:'20:00'},
      {id:'139607619',from:'18:00',until:'21:00'},
      {id:'139607618',from:'21:00',until:'23:45'}
    ];
    return { monday:slots,tuesday:slots,wednesday:slots,thursday:slots,friday:slots,saturday:slots,sunday:slots,blackouts:[] };
  }

  // Pickup is a single moment ("come by at 2pm"), not a window like
  // delivery — the fallback schedule used for any date beyond Zapiet's
  // real minDateSlots (see renderPickupSlotsForDate()) needs its own
  // on-the-hour slots instead of reusing defaultDeliverySchedule()'s
  // ranges.
  // LAST-RESORT fallback only — a direct API call showed Zapiet's real
  // pickup hours are 07:00-23:00 every day (via `daysOfWeek`), not this
  // 9:30-21:00 range. buildPickupScheduleFromDaysOfWeek() replaces this
  // with the real hours whenever the calendar request succeeds; this
  // hand-kept copy only matters if that request fails outright.
  defaultPickupSchedule() {
    const hours = [
      { id: 'pickup-hour-0930', from: '09:30', until: '10:00' },
      { id: 'pickup-hour-10', from: '10:00', until: '11:00' },
      { id: 'pickup-hour-11', from: '11:00', until: '12:00' },
      { id: 'pickup-hour-12', from: '12:00', until: '13:00' },
      { id: 'pickup-hour-13', from: '13:00', until: '14:00' },
      { id: 'pickup-hour-14', from: '14:00', until: '15:00' },
      { id: 'pickup-hour-15', from: '15:00', until: '16:00' },
      { id: 'pickup-hour-16', from: '16:00', until: '17:00' },
      { id: 'pickup-hour-17', from: '17:00', until: '18:00' },
      { id: 'pickup-hour-18', from: '18:00', until: '19:00' },
      { id: 'pickup-hour-19', from: '19:00', until: '20:00' },
      { id: 'pickup-hour-20', from: '20:00', until: '21:00' },
    ];
    return { monday:hours,tuesday:hours,wednesday:hours,thursday:hours,friday:hours,saturday:hours,sunday:hours,blackouts:[] };
  }

  // NOTE: deliberately does NOT set .value on either field — only .min,
  // as a lower bound for the picker. The Delivery date/time used to be
  // pre-filled with today's date, which (a) looked like a real choice
  // the customer never actually made, and (b) could go stale: if the
  // real Zapiet calendar data arrived and rebuilt the time <select>
  // AFTER a fallback slot had already been auto-matched, _ZapietId could
  // end up pointing at a slot that no longer existed in the dropdown,
  // blocking Add to Cart with "Select date, time and validate the
  // delivery address" even though the fields visually looked filled.
  // Leaving both empty until the customer explicitly picks removes that
  // whole class of bug — every value in the form is now the result of a
  // real 'change' event, which is the only thing that sets _ZapietId.
  setMinimumDates() {
    const d = new Date();
    const min = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    if (this.deliveryDate) this.deliveryDate.min = min;
    if (this.pickupDate) this.pickupDate.min = min;
  }

  bindEvents() {
    // Switching tabs used to only toggle which panel is visible — it never
    // re-synced _ZapietId for whatever was already selected in that panel
    // (e.g. a pickup time that got auto-picked while Delivery was still
    // the active tab, see populatePickupSlots()). Customers who only ever
    // interacted with Delivery-vs-Pickup — never touching the pickup time
    // dropdown itself, since there's nothing else to pick when there's
    // only one location — could hit "Select pickup location, date and
    // time" at Add to Cart despite everything already looking selected.
    this.methodButtons.forEach((button) => button.addEventListener('click', async () => {
      this.setMethod(button.dataset.deliveryMethod);
      await this.handleSelectionChange();
    }));
    this.deliveryDate?.addEventListener('change', () => { this.clearZapietId(); this.renderDeliverySlotsForDate(); });
    this.deliveryTime?.addEventListener('change', () => this.handleSelectionChange());
    this.deliveryAddressSubmit?.addEventListener('click', () => this.validateDeliveryAddress());
    this.deliveryAddress?.addEventListener('keydown', (event) => { if (event.key === 'Enter') { event.preventDefault(); this.validateDeliveryAddress(); } });
    this.deliveryAddress?.addEventListener('input', () => { if (this.deliveryAddress.value.trim() !== this.deliveryValidatedAddress) this.resetAddressResult(); });
    this.pickupLocation?.addEventListener('change', () => this.handlePickupLocationChange());
    this.pickupDate?.addEventListener('change', () => this.handlePickupDateChange());
    this.pickupTime?.addEventListener('change', () => this.handleSelectionChange());
    const guardSubmit = (event) => {
      if (!this.validate()) { event.preventDefault(); event.stopImmediatePropagation(); this.element.scrollIntoView({behavior:'smooth',block:'center'}); }
    };
    this.form.addEventListener('submit', guardSubmit, true);
    // The sticky add-to-cart bar (non-minimalist style) renders its own
    // separate <form action="/cart/add">, distinct from this.form — it
    // isn't nested inside it and doesn't share its submit event, so it
    // was bypassing this validation entirely. Guard every other
    // /cart/add form on the page the same way so switching to Add To
    // Cart from the sticky bar also requires address/date/time first.
    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      if (form === this.form) return;
      form.addEventListener('submit', guardSubmit, true);
    });
  }

  getShoppingCart() {
    const variantId = Number(this.form.querySelector('[name="id"]')?.value);
    const productId = Number(this.element.dataset.productId);
    const quantity = Number(this.form.querySelector('[name="quantity"]')?.value) || 1;
    return variantId ? [{variant_id:variantId,product_id:productId||null,sku:null,quantity}] : [];
  }

  async initializeDeliveryCalendar() {
    try {
      const url = new URL(`https://api.zapiet.com/v1.0/delivery/locations/${this.deliveryLocationId}/calendar`);
      url.searchParams.set('shop', this.shopDomain);
      const response = await fetch(url.toString(), {method:'POST',headers:{Accept:'application/json','Content-Type':'application/json; charset=UTF-8'},body:JSON.stringify({shoppingCart:this.getShoppingCart()})});
      if (!response.ok) return;
      const data = await response.json();
      // Zapiet's own timezone for this location — confirmed via a direct
      // API call to be 'America/Boise', NOT the 'America/Los_Angeles'
      // hardcoded above in window.LUCIOL_LEAD_TIME. That 1-hour gap
      // shifted the lead-time cutoff (isDateToday()/filterSlotsByLeadTime()
      // below) an hour early, which is what made the last delivery
      // window quietly disappear 2-3 hours before actual closing instead
      // of ~1 hour before it.
      if (data.timezone) {
        window.LUCIOL_LEAD_TIME = window.LUCIOL_LEAD_TIME || {};
        window.LUCIOL_LEAD_TIME.storeTimeZone = data.timezone;
      }
      // Zapiet's calendar response includes the FULL weekly schedule
      // under `slots` (every day_of_week, not just the nearest available
      // date) — confirmed via a direct API call. Real, live source of
      // truth; defaultDeliverySchedule()'s hand-kept copy only matters
      // if this request fails outright.
      if (Array.isArray(data.slots) && data.slots.length) {
        Object.assign(this.deliverySchedule, groupSlotsByWeekday(data.slots));
      }
      // Only ever raises .value if it was already set to something too
      // early (never sets it from empty — see setMinimumDates() above).
      if (data.minDate) {
        this.deliveryDate.min = data.minDate;
        if (this.deliveryDate.value && this.deliveryDate.value < data.minDate) this.deliveryDate.value = data.minDate;
      }
      if (Array.isArray(data.disabled)) {
        const disabledDates = normalizeDisabledDates(data.disabled);
        this.deliverySchedule.disabled = disabledDates;
        // Blocks those dates in the calendar UI itself — mirrors the
        // cart page's initializeDeliveryCalendar() fix. renderDeliverySlotsForDate()
        // below is the second layer for a disabled date that reaches
        // .value some other way (URL prefill, programmatic set).
        if (this.deliveryDate?._flatpickr) this.deliveryDate._flatpickr.set('disable', disabledDates);
      }
      this.deliveryMinDate = data.minDate || null;
      this.deliveryMinDateSlots = Array.isArray(data.minDateSlots)
        ? data.minDateSlots.map((s) => ({ id: s.id, from: s.available_from, until: s.available_until }))
        : null;
      // A date may already be selected here (e.g. the customer picked
      // one before this network call resolved) — refresh its slots now
      // that the real Zapiet data is in, and clear _ZapietId so a stale
      // fallback-schedule slot can't silently pass validation once the
      // dropdown's options have changed under it.
      if (this.deliveryDate.value) {
        this.clearZapietId();
        this.renderDeliverySlotsForDate();
      }
    } catch (e) { console.info('[ProductDeliverySelector] Delivery calendar fallback is active.'); }
  }

  renderDeliverySlotsForDate() {
    const date = this.deliveryDate?.value;
    if (!date) return;
    // Second layer of enforcement for Zapiet's excluded dates (see the
    // flatpickr 'disable' set in initializeDeliveryCalendar()).
    if ((this.deliverySchedule.disabled || []).includes(date)) {
      this.renderDeliverySlots([]);
      this.showError('Delivery is not available on this date. Please choose another day.');
      return;
    }
    if (this.deliveryMinDateSlots && date === this.deliveryMinDate) {
      this.renderDeliverySlots(this.deliveryMinDateSlots);
      return;
    }
    const weekday = new Intl.DateTimeFormat('en-US',{weekday:'long'}).format(new Date(`${date}T12:00:00`)).toLowerCase();
    this.renderDeliverySlots(this.deliverySchedule[weekday] || []);
  }

  // True only when dateStr (Y-m-d) is today in the STORE's timezone —
  // deliberately not the visitor's device/browser timezone. Future
  // dates need no lead-time filtering at all.
  isDateToday(dateStr) {
    if (!dateStr) return false;
    const timeZone = window.LUCIOL_LEAD_TIME?.storeTimeZone || 'America/Los_Angeles';
    return dateStr === getStoreNow(timeZone).dateStr;
  }

  // Drops any slot that starts at or before "now + leadTimeHours",
  // measured in the store's own timezone, when the selected date is
  // today there. For delivery windows this also removes any window the
  // cutoff simply falls inside of (e.g. cutoff 14:00 hides a
  // 09:00-15:00 window, not just anything before 09:00) — a window
  // that's already partway "expired" isn't offered as if the whole
  // thing were still available. Pickup's fallback slots are single
  // points in time (from === until), so the same rule just means "at
  // least leadTimeHours from now, Seattle time".
  filterSlotsByLeadTime(slots, dateStr, leadTimeHours, fromKey = 'from') {
    if (!this.isDateToday(dateStr)) return slots;
    const timeZone = window.LUCIOL_LEAD_TIME?.storeTimeZone || 'America/Los_Angeles';
    const cutoffMinutes = getStoreNow(timeZone).minutes + leadTimeHours * 60;
    return slots.filter((slot) => {
      const time = slot[fromKey];
      if (!time) return true;
      const [h, m] = time.split(':').map(Number);
      return h * 60 + m > cutoffMinutes;
    });
  }

  // Renders the available slots for the selected date directly as
  // <option> elements in the native <select data-delivery-time> — a
  // real dropdown, same pattern as the pickup time select below
  // (populatePickupSlots), instead of the previous custom pill list.
  renderDeliverySlots(slots) {
    if (!this.deliveryTime) return;
    const leadTimeHours = window.LUCIOL_LEAD_TIME?.deliveryHours ?? 1;
    const available = this.filterSlotsByLeadTime(slots, this.deliveryDate?.value, leadTimeHours, 'from');
    this.deliveryTime.innerHTML = '<option value="">Choose a time</option>';
    available.forEach((slot) => {
      const label = `${this.formatDisplayTime(slot.from)} - ${this.formatDisplayTime(slot.until)}`;
      const option = document.createElement('option');
      option.value = label;
      option.textContent = label;
      option.dataset.slotId = String(slot.id || '');
      option.dataset.startTime = slot.from;
      option.dataset.endTime = slot.until;
      this.deliveryTime.appendChild(option);
    });
  }

  /**
   * Distance calculation via Google Distance Matrix Service.
   * Replaces the old ZIP-lookup based rate resolution.
   */
  computeDistanceMiles(destination) {
    return new Promise((resolve, reject) => {
      if (!window.google?.maps?.DistanceMatrixService) {
        reject(new Error('Google Maps DistanceMatrixService unavailable'));
        return;
      }
      const service = new google.maps.DistanceMatrixService();
      service.getDistanceMatrix(
        {
          origins: [this.storeAddress],
          destinations: [destination],
          travelMode: 'DRIVING',
          unitSystem: google.maps.UnitSystem.IMPERIAL,
        },
        (response, status) => {
          if (status !== 'OK') return reject(new Error('Distance Matrix request failed'));
          const element = response.rows?.[0]?.elements?.[0];
          if (!element || element.status !== 'OK') return reject(new Error('No route found for this address'));
          resolve({
            miles: element.distance.value / 1609.34,
            durationText: element.duration.text,
          });
        }
      );
    });
  }

  /**
   * Computes delivery price from a mile distance using the flat-fee
   * distance tiers in window.LUCIOL_DELIVERY_RATES, with free-delivery
   * rules (order subtotal + distance) checked first.
   */
  resolveRateByMiles(miles, subtotalDollars) {
    const config = window.LUCIOL_DELIVERY_RATES || {};
    const tiers = config.tiers || [];
    const maxMiles = Number(config.maxMiles ?? Infinity);
    const freeRules = config.freeDeliveryRules || [];

    if (miles > maxMiles) {
      return { miles, eligible: false };
    }

    const subtotal = Number(subtotalDollars) || 0;
    const isFree = freeRules.some((rule) => subtotal >= rule.minSubtotal && miles <= rule.withinMiles);
    if (isFree) {
      return { miles, price: 0, eligible: true };
    }

    const tier = tiers.find((t) => miles <= t.upTo);
    if (!tier) {
      return { miles, eligible: false };
    }

    return { miles, price: tier.price, eligible: true };
  }

  async validateDeliveryAddress() {
    const query = this.deliveryAddress?.value.trim();

    if (!query) {
      return this.showDeliveryStatus('Please enter a delivery address.', 'error');
    }

    try {
      this.setDeliveryLoading(true);

      const [{ miles, durationText }, geocoded] = await Promise.all([
        this.computeDistanceMiles(query),
        geocodeAddress(query),
      ]);
      if (geocoded) this.lastPlaceAddressComponents = geocoded;

      this.deliveryRate = this.resolveRateByMiles(miles, this.getCurrentProductSubtotalCents() / 100);

      if (!this.deliveryRate?.eligible) {
        this.resetAddressResult();
        const message = window.LUCIOL_DELIVERY_RATES?.outOfRangeLabel || 'Delivery is not available for this address.';
        return this.showDeliveryStatus(message, 'error');
      }

      this.deliveryLocation = { id: this.deliveryLocationId };
      this.deliveryValidatedAddress = query;

      if (this.deliverySelectedAddressText) {
        this.deliverySelectedAddressText.textContent = `${query} (${miles.toFixed(1)} mi, ${durationText})`;
      }
      if (this.deliverySelectedAddress) {
        this.deliverySelectedAddress.hidden = false;
      }

      this.updateRateAndTotal();
      this.showMap(query);

      // Share this address with the cart-level delivery calculator via
      // sessionStorage, so it's already prefilled when the customer
      // opens the cart, within the same browser tab session.
      const components = extractAddressComponents(this.lastPlaceAddressComponents);
      saveToCartSession(query, components);

      await this.handleSelectionChange();

      this.showDeliveryStatus('Delivery is available.', 'success');
    } catch (error) {
      console.error('[ProductDeliverySelector] Delivery validation failed:', error);
      this.resetAddressResult();
      this.showDeliveryStatus('We could not validate this address. Please try again.', 'error');
    } finally {
      this.setDeliveryLoading(false);
    }
  }

  initializeAddressAutocomplete() {
    if (!this.deliveryAddress || !window.google?.maps?.places?.Autocomplete) {
      return;
    }

    this.googleAutocomplete = new window.google.maps.places.Autocomplete(this.deliveryAddress, {
      componentRestrictions: { country: 'us' },
      fields: ['formatted_address', 'address_components', 'geometry'],
      types: ['address'],
    });

    this.googleAutocomplete.addListener('place_changed', () => {
      const place = this.googleAutocomplete.getPlace();
      const postalComponent = place.address_components?.find((component) => component.types?.includes('postal_code'));

      this.resetAddressResult();

      this.deliveryPostalCode = postalComponent?.long_name || '';
      this.selectedLatLng = place.geometry?.location || null;
      this.lastPlaceAddressComponents = place.address_components || null;

      const selectedAddress = place.formatted_address || this.deliveryAddress.value.trim();
      if (selectedAddress) {
        this.deliveryAddress.value = selectedAddress;
      }

      // The search/submit button is hidden in the restyled layout (Figma
      // node 5:114 has no visible search icon), so picking a suggestion
      // from the dropdown must trigger the price calculation itself,
      // same as the cart-level calculator already does.
      this.validateDeliveryAddress();
    });
  }

  getCurrentProductSubtotalCents() {
    const quantity = Number(this.form.querySelector('[name="quantity"]')?.value) || 1;
    return this.productPriceCents * quantity;
  }

  updateRateAndTotal() {
    const config = window.LUCIOL_DELIVERY_RATES || {};
    const subtotalCents = this.getCurrentProductSubtotalCents();

    let rateLabel = config.fallbackLabel || 'Calculated at checkout';
    let totalCents = subtotalCents;

    if (this.deliveryRate?.eligible) {
      const rateCents = Math.round(this.deliveryRate.price * 100);
      rateLabel = rateCents === 0 ? 'Free' : this.money(rateCents);
      totalCents += rateCents;
    } else if (this.deliveryRate && !this.deliveryRate.eligible) {
      rateLabel = config.outOfRangeLabel || 'Delivery is not available for this address';
    }

    this.deliveryRateText.textContent = rateLabel;
    this.orderTotalText.textContent = this.money(totalCents);
    this.deliverySummary.hidden = false;
    this.propertyInputs['_Estimated delivery fee'].value = rateLabel;

    // The hidden "Home Delivery" fee line item (see syncDeliveryFeeItem
    // above) is now synced once, right before checkout — see the
    // checkout-button script in cart-drawer.liquid. Doing it live on
    // every address/date keystroke used to leave the already-open cart
    // drawer's own item list showing a stale quantity (Shopify doesn't
    // auto-refresh that list just because a cart/change.js call
    // happened elsewhere), which looked like a bug even though the real
    // cart total was correct. Checking out is the one moment a fresh
    // page load happens anyway, so there's nothing stale to show.
  }

  showMap(destination) {
    if (!this.deliveryMap) return;
    this.deliveryMap.hidden = false;

    const canvas = this.element.querySelector('[data-delivery-map-canvas]');
    if (!canvas || !window.google?.maps) return;

    if (!this.map) {
      this.map = new google.maps.Map(canvas, { zoom: 10 });
      this.directionsRenderer = new google.maps.DirectionsRenderer();
      this.directionsRenderer.setMap(this.map);
    }

    const directionsService = new google.maps.DirectionsService();
    directionsService.route(
      {
        origin: this.storeAddress,
        destination: destination,
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === 'OK') {
          this.directionsRenderer.setDirections(result);
        }
      }
    );
  }

  money(cents) { try { return new Intl.NumberFormat('en-US',{style:'currency',currency:this.currency}).format(cents/100); } catch { return `$${(cents/100).toFixed(2)}`; } }

  resetAddressResult() {
    this.deliveryLocation=null; this.deliveryValidatedAddress=''; this.deliveryRate=null; this.deliveryPostalCode=''; this.selectedLatLng=null; this.clearZapietId();
    if(this.deliverySelectedAddress) this.deliverySelectedAddress.hidden=true;
    // The Cost box stays visible at all times in the restyled layout
    // (Figma node 5:114 pairs Address + Cost in one row) — only the
    // text resets to a placeholder instead of hiding the box.
    if(this.deliveryRateText) this.deliveryRateText.textContent = '—';
    if(this.deliveryMap) this.deliveryMap.hidden=true;
  }

  async initializePickup() {
    if (!this.pickupLocation) return;
    try { this.setPickupLoading(true); await this.loadPickupLocations(); this.populatePickupLocations();
      const first=this.pickupLocations[0]; if(!first) throw new Error('No pickup locations');
      this.pickupLocation.value=String(first.id); this.updatePickupCard(first); await this.loadPickupCalendar(first.id); this.populatePickupCalendar();
    } catch(e){ console.error(e); this.showError('Pickup availability could not be loaded.'); } finally { this.setPickupLoading(false); }
  }
  async loadPickupLocations(){const u=new URL('https://api.zapiet.com/v1.0/pickup/locations');u.searchParams.set('shop',this.shopDomain);const r=await fetch(u,{method:'POST',headers:{Accept:'application/json','Content-Type':'application/json; charset=UTF-8'},body:JSON.stringify({geoSearchQuery:'',shoppingCart:this.getShoppingCart()})});if(!r.ok)throw new Error('Pickup locations failed');const d=await r.json();this.pickupLocations=Array.isArray(d.locations)?d.locations:[];}
  async loadPickupCalendar(id){const u=new URL(`https://api.zapiet.com/v1.0/pickup/locations/${id}/calendar`);u.searchParams.set('shop',this.shopDomain);const r=await fetch(u,{method:'POST',headers:{Accept:'application/json','Content-Type':'application/json; charset=UTF-8'},body:JSON.stringify({shoppingCart:this.getShoppingCart()})});if(!r.ok)throw new Error('Pickup calendar failed');this.pickupCalendar=await r.json();}
  populatePickupLocations(){this.pickupLocation.innerHTML='';this.pickupLocations.forEach(l=>{const o=document.createElement('option');o.value=String(l.id);o.textContent=l.company_name||'Pickup';Object.assign(o.dataset,{company:l.company_name||'',addressLine1:l.address_line_1||'',addressLine2:l.address_line_2||'',city:l.city||'',region:l.region||'',postalCode:l.postal_code||'',country:l.country||'',phone:l.phone||''});this.pickupLocation.appendChild(o);});}
  updatePickupCard(l){if(!l)return;this.pickupCompany.textContent=l.company_name||'';this.pickupAddress.textContent=[l.address_line_1,l.address_line_2].filter(Boolean).join(', ');this.pickupCity.textContent=[l.city,l.postal_code].filter(Boolean).join(', ');this.pickupMapLink.href=`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([l.address_line_1,l.city,l.region,l.postal_code].filter(Boolean).join(' '))}`;}
  populatePickupCalendar(){
    // Same timezone fix as initializeDeliveryCalendar() — Zapiet's
    // pickup calendar response carries its own `timezone` too.
    if (this.pickupCalendar.timezone) {
      window.LUCIOL_LEAD_TIME = window.LUCIOL_LEAD_TIME || {};
      window.LUCIOL_LEAD_TIME.storeTimeZone = this.pickupCalendar.timezone;
    }
    // Zapiet's pickup calendar gives `daysOfWeek` (min/max open-close
    // per weekday — confirmed via a direct API call: 07:00-23:00 every
    // day for this location, NOT the 09:30-21:00 defaultPickupSchedule()
    // guessed), not a flat slot list like delivery.
    // buildPickupScheduleFromDaysOfWeek() expands it into real hourly
    // slots — the live source of truth whenever present.
    if (this.pickupCalendar.daysOfWeek) {
      const built = buildPickupScheduleFromDaysOfWeek(this.pickupCalendar.daysOfWeek);
      if (built) Object.assign(this.pickupSchedule, built);
    }
    this.pickupDate.min=this.pickupCalendar.minDate||'';
    // No longer auto-writing minDate into this.pickupDate.value here —
    // a raw DOM write bypasses flatpickr, so the visible field kept
    // showing its empty placeholder while a real date (and, via
    // populatePickupSlots()'s auto-select, a real time + _ZapietId) was
    // already set underneath it — letting Add to Cart through with no
    // pickup date the customer ever actually picked. Slots now wait for
    // an actual date, via the pickupDate 'change' handler.
    if (this.pickupDate._flatpickr && this.pickupCalendar.minDate) {
      this.pickupDate._flatpickr.set('minDate', this.pickupCalendar.minDate);
    }
    // Same Zapiet-excluded-dates enforcement as delivery.
    if (Array.isArray(this.pickupCalendar.disabled)) {
      const disabledDates = normalizeDisabledDates(this.pickupCalendar.disabled);
      this.pickupSchedule.disabled = disabledDates;
      if (this.pickupDate._flatpickr) this.pickupDate._flatpickr.set('disable', disabledDates);
    }
  }
  renderPickupSlotsForDate(){
    const date = this.pickupDate?.value;
    if (!date) return;
    // Second layer of enforcement for a disabled date reaching .value
    // some other way (see renderDeliverySlotsForDate()'s equivalent).
    if ((this.pickupSchedule.disabled || []).includes(date)) {
      this.populatePickupSlots([]);
      this.showError('Pickup is not available on this date. Please choose another day.');
      return;
    }
    const weekday = new Intl.DateTimeFormat('en-US',{weekday:'long'}).format(new Date(`${date}T12:00:00`)).toLowerCase();
    // this.pickupSchedule reuses the delivery default schedule shape
    // ({from, until}) — populatePickupSlots() expects Zapiet's own
    // {available_from, available_until} shape, so map between them.
    const slots = (this.pickupSchedule[weekday] || []).map((s) => ({ available_from: s.from, available_until: s.until }));
    this.populatePickupSlots(slots);
  }
  populatePickupSlots(slots){
    const leadTimeHours = window.LUCIOL_LEAD_TIME?.pickupHours ?? 2;
    const available = this.filterSlotsByLeadTime(slots, this.pickupDate?.value, leadTimeHours, 'available_from');
    this.pickupTime.innerHTML='<option value="">Choose a pickup time</option>';
    available.forEach(s=>{const o=document.createElement('option');o.value=s.available_from===s.available_until?this.formatDisplayTime(s.available_from):`${this.formatDisplayTime(s.available_from)} - ${this.formatDisplayTime(s.available_until)}`;o.textContent=o.value;o.dataset.startTime=s.available_from;o.dataset.endTime=s.available_until;this.pickupTime.appendChild(o);});
    // Only one pickup location ever exists, so it's already auto-selected
    // by initializePickup() — customers see nothing left to "choose" and
    // often never touch this time dropdown at all. Pre-select the first
    // slot here too (mirrors renderDeliverySlots' equivalent gap) so
    // there's always a value ready the moment they land on/switch to the
    // Pickup tab, instead of silently staying blank until a manual click.
    const first = Array.from(this.pickupTime.options).find((o) => o.value);
    if (first) this.pickupTime.value = first.value;
  }
  async handlePickupLocationChange(){const id=this.pickupLocation.value;const l=this.pickupLocations.find(x=>String(x.id)===String(id));this.updatePickupCard(l);await this.loadPickupCalendar(id);this.populatePickupCalendar();this.clearZapietId();await this.handleSelectionChange();}
  async handlePickupDateChange(){if(this.pickupDate.value===this.pickupCalendar?.minDate)this.populatePickupSlots(this.pickupCalendar.minDateSlots||[]);else this.renderPickupSlotsForDate();this.clearZapietId();this.clearError();await this.handleSelectionChange();}

  setMethod(method){this.method=method;this.methodButtons.forEach(b=>{const a=b.dataset.deliveryMethod===method;b.classList.toggle('is-active',a);b.setAttribute('aria-pressed',String(a));});this.deliveryPanel.hidden=method!=='delivery';this.pickupPanel.hidden=method!=='pickup';this.clearZapietId();this.clearError();}
  async handleSelectionChange(){const s=this.method==='pickup'?this.getPickupSelection():this.getDeliverySelection();if(!s){this.clearZapietId();return;}this.setZapietId(s.zapietId);await this.saveCartAttributes(s.attributes);}
  getDeliverySelection(){const o=this.deliveryTime.selectedOptions[0],id=this.deliveryLocation?.id,date=this.deliveryDate.value,time=this.deliveryTime.value;if(!this.deliveryValidatedAddress||!id||!date||!time||!o?.dataset.slotId)return null;this.propertyInputs['_Delivery address'].value=this.deliveryValidatedAddress;this.propertyInputs['_Delivery date'].value=date;this.propertyInputs['_Delivery time'].value=time;return{zapietId:this.buildZapietId('D',id,date,o.dataset.startTime,o.dataset.endTime),attributes:{'Delivery-Location-Id':String(id),'Delivery-Date':this.formatCartDate(date),'Delivery-Time':time,'Delivery-Slot-Id':String(o.dataset.slotId),'Delivery-Widget-Query':this.deliveryValidatedAddress,'Delivery-Address':this.deliveryValidatedAddress,'Delivery-Miles':this.deliveryRate?.miles?String(this.deliveryRate.miles.toFixed(1)):'','Estimated-Delivery-Rate':this.deliveryRate?.eligible?String(this.deliveryRate.price):'','Checkout-Method':'delivery','Pickup-Location-Id':'','Pickup-Date':'','Pickup-Time':''}};}
  getPickupSelection(){const o=this.pickupLocation.selectedOptions[0],t=this.pickupTime.selectedOptions[0],id=this.pickupLocation.value,date=this.pickupDate.value,time=this.pickupTime.value;if(!id||!date||!time||!t?.dataset.startTime)return null;
    // Mirror validateDeliveryAddress()'s saveToCartSession() call for Delivery —
    // without this, Pickup's address only ever reached cart ATTRIBUTES (our own
    // UI), never the sessionStorage key cart-drawer.liquid's checkout button
    // reads to prefill checkout[shipping_address], so Pickup left checkout blank.
    saveToCartSession([o.dataset.addressLine1,o.dataset.addressLine2,o.dataset.city,o.dataset.postalCode].filter(Boolean).join(', '),{address1:o.dataset.addressLine1||'',city:o.dataset.city||'',province:o.dataset.region||'',zip:o.dataset.postalCode||'',country:o.dataset.country||''});
    return{zapietId:this.buildZapietId('P',id,date,t.dataset.startTime,t.dataset.endTime),attributes:{'Pickup-Location-Id':id,'Pickup-Date':this.formatCartDate(date),'Pickup-Time':time,'Pickup-Location-Company':o.dataset.company||'','Pickup-Location-Address-Line-1':o.dataset.addressLine1||'','Pickup-Location-Address-Line-2':o.dataset.addressLine2||'','Pickup-Location-City':o.dataset.city||'','Pickup-Location-Region':o.dataset.region||'','Pickup-Location-Postal-Code':o.dataset.postalCode||'','Pickup-Location-Country':o.dataset.country||'','Pickup-Location-Phone':o.dataset.phone||'','Checkout-Method':'pickup','Delivery-Location-Id':'','Delivery-Date':'','Delivery-Time':'','Delivery-Slot-Id':''}};}
  buildZapietId(m,l,d,s,e){return `M=${m}&L=${l}&D=${d}T${s}:00Z&E=${e}`;}
  formatCartDate(d){return d.replaceAll('-','/');} formatDisplayTime(t){const[h,m]=t.split(':');const n=Number(h);return `${n%12||12}:${m} ${n>=12?'PM':'AM'}`;}
  async saveCartAttributes(attributes){this.isSaving=true;try{const r=await fetch(`${window.Shopify?.routes?.root||'/'}cart/update.js`,{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({attributes})});if(!r.ok)throw new Error('Cart update failed');return r.json();}finally{this.isSaving=false;}}
  validate(){if(this.isSaving||this.isLoadingDelivery||this.isLoadingPickup){this.showError('Please wait while your shipping selection is saved.');return false;}if(!this.zapietIdInput?.value){this.showError(this.method==='delivery'?'Select date, time and validate the delivery address.':'Select pickup location, date and time.');return false;}return true;}
  setDeliveryLoading(v){this.isLoadingDelivery=v;this.element.classList.toggle('is-loading',v||this.isLoadingPickup||this.isSaving);} setPickupLoading(v){this.isLoadingPickup=v;this.element.classList.toggle('is-loading',v||this.isLoadingDelivery||this.isSaving);}
  setZapietId(v){if(this.zapietIdInput)this.zapietIdInput.value=v;} clearZapietId(){if(this.zapietIdInput)this.zapietIdInput.value='';}
  showDeliveryStatus(m,t){this.deliveryAddressStatus.textContent=m;this.deliveryAddressStatus.hidden=false;this.deliveryAddressStatus.className=`product-delivery-address__status is-${t}`;} showError(m){this.errorElement.textContent=m;this.errorElement.hidden=false;} clearError(){this.errorElement.hidden=true;this.errorElement.textContent='';}
}
function init(root=document){root.querySelectorAll('[data-product-delivery-selector]').forEach(el=>{if(el.dataset.initialized)return;el.dataset.initialized='true';new ProductDeliverySelector(el);});}
document.addEventListener('DOMContentLoaded',()=>init());document.addEventListener('shopify:section:load',e=>init(e.target));